import { useQuery } from '@tanstack/react-query';
import { TrendingUp, TrendingDown, AlertTriangle, Users } from 'lucide-react';
import { api } from '@/lib/api';
import { cn, formatPercent, getStatusColor, getUtilizationColor } from '@/lib/utils';

export function Dashboard() {
  const { data: positionSummary } = useQuery({
    queryKey: ['positions', 'summary'],
    queryFn: () => api.getPositionSummary(),
  });

  const { data: alertSummary } = useQuery({
    queryKey: ['alerts', 'summary'],
    queryFn: () => api.getAlertSummary(),
  });

  const summary = positionSummary?.summary || [];
  const alerts = alertSummary || { total: 0, unacknowledged: 0, bySeverity: { info: 0, warning: 0, critical: 0 } };

  // Calculate stats
  const totalClients = summary.length;
  const totalBreaches = summary.reduce((acc, s) => acc + s.breachCount, 0);
  const totalWarnings = summary.reduce((acc, s) => acc + s.warningCount, 0);
  const avgUtilization = summary.length > 0
    ? summary.reduce((acc, s) => acc + s.maxUtilization, 0) / summary.length
    : 0;

  return (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Total Clients"
          value={totalClients.toString()}
          icon={Users}
          trend={null}
        />
        <StatCard
          title="Avg Utilization"
          value={formatPercent(avgUtilization)}
          icon={TrendingUp}
          trend={avgUtilization > 50 ? 'up' : 'down'}
        />
        <StatCard
          title="Active Breaches"
          value={totalBreaches.toString()}
          icon={AlertTriangle}
          trend={null}
          variant={totalBreaches > 0 ? 'destructive' : 'default'}
        />
        <StatCard
          title="Warnings"
          value={totalWarnings.toString()}
          icon={AlertTriangle}
          trend={null}
          variant={totalWarnings > 0 ? 'warning' : 'default'}
        />
      </div>

      {/* Position Summary Table */}
      <div className="rounded-lg border bg-card">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="font-semibold">Position Summary by Client</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="px-6 py-3 text-left text-sm font-medium text-muted-foreground">Client</th>
                <th className="px-6 py-3 text-left text-sm font-medium text-muted-foreground">Positions</th>
                <th className="px-6 py-3 text-left text-sm font-medium text-muted-foreground">Max Utilization</th>
                <th className="px-6 py-3 text-left text-sm font-medium text-muted-foreground">Status</th>
                <th className="px-6 py-3 text-left text-sm font-medium text-muted-foreground">Breaches</th>
              </tr>
            </thead>
            <tbody>
              {summary.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-8 text-center text-muted-foreground">
                    No position data available
                  </td>
                </tr>
              ) : (
                summary.map((client) => (
                  <tr key={client.clientId} className="border-b last:border-0">
                    <td className="px-6 py-4 font-medium">{client.clientName}</td>
                    <td className="px-6 py-4">{client.totalPositions}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <div className="h-2 w-24 rounded-full bg-muted overflow-hidden">
                          <div
                            className={cn('h-full', getUtilizationColor(client.maxUtilization))}
                            style={{ width: `${Math.min(client.maxUtilization, 100)}%` }}
                          />
                        </div>
                        <span className="text-sm">{formatPercent(client.maxUtilization)}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={cn('px-2 py-1 rounded-full text-xs font-medium', getStatusColor(client.status))}>
                        {client.status}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {client.breachCount > 0 && (
                        <span className="text-red-600 font-medium">{client.breachCount}</span>
                      )}
                      {client.breachCount === 0 && '-'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Alert Summary */}
      <div className="rounded-lg border bg-card p-6">
        <h2 className="font-semibold mb-4">Alert Summary</h2>
        <div className="grid gap-4 md:grid-cols-3">
          <div className="flex items-center gap-4 p-4 rounded-lg bg-blue-50">
            <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
              <span className="text-blue-600 font-semibold">{alerts.bySeverity.info}</span>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Info</p>
              <p className="font-medium">Informational Alerts</p>
            </div>
          </div>
          <div className="flex items-center gap-4 p-4 rounded-lg bg-yellow-50">
            <div className="h-10 w-10 rounded-full bg-yellow-100 flex items-center justify-center">
              <span className="text-yellow-600 font-semibold">{alerts.bySeverity.warning}</span>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Warning</p>
              <p className="font-medium">Warning Alerts</p>
            </div>
          </div>
          <div className="flex items-center gap-4 p-4 rounded-lg bg-red-50">
            <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center">
              <span className="text-red-600 font-semibold">{alerts.bySeverity.critical}</span>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Critical</p>
              <p className="font-medium">Critical Alerts</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

interface StatCardProps {
  title: string;
  value: string;
  icon: React.ElementType;
  trend: 'up' | 'down' | null;
  variant?: 'default' | 'destructive' | 'warning';
}

function StatCard({ title, value, icon: Icon, trend, variant = 'default' }: StatCardProps) {
  return (
    <div className="rounded-lg border bg-card p-6">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-muted-foreground">{title}</p>
        <Icon className={cn(
          'h-4 w-4',
          variant === 'destructive' && 'text-red-500',
          variant === 'warning' && 'text-yellow-500',
          variant === 'default' && 'text-muted-foreground'
        )} />
      </div>
      <div className="mt-2 flex items-baseline gap-2">
        <p className={cn(
          'text-2xl font-semibold',
          variant === 'destructive' && 'text-red-600',
          variant === 'warning' && 'text-yellow-600'
        )}>
          {value}
        </p>
        {trend && (
          <span className={cn(
            'flex items-center text-sm',
            trend === 'up' ? 'text-green-600' : 'text-red-600'
          )}>
            {trend === 'up' ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
          </span>
        )}
      </div>
    </div>
  );
}
